<template>
  <div class="stat-card-white" id="etmf-pie" ref="captureArea">
    <div class="stat-header-white">
      <span>{{ computedChartLabel }}</span>

      <div class="d-flex align-items-center" v-if="!loader">
        <select class="graph-select py-0 ml-2" v-model="selectedRoleKey">
          <option v-for="opt in roleOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>

        <div class="expand-block px-2 mb-1 position-relative">
          <img class="cursor-pointer" @click="showtable()" src="../../assets//img/table_view.png" alt="More block" />
          <div class="more-expand">
            <span>{{ $t ? $t('navigations.data overview') : 'Data overview' }}</span>
          </div>
        </div>

        <div class="expand-block px-2 mb-1 cursor-pointer" @click.prevent="expandBlock = true">
          <img src="../../assets//img/expand.png" alt="Expand block" />
        </div>

        <div class="text-center" v-on:clickout="showVisitPopup = false">
          <div class="position-relative cursor-pointer biggerfont ml-2" @click.prevent="showVisitPopup = !showVisitPopup">
            <i class="fa fa-ellipsis-v"></i>
          </div>
          <div class="plan-side-popup" ref="excludeThis" v-if="showVisitPopup">
            <div @click.prevent="showVisitPopup = false, exporttoexcell()">Export to Excel</div>
            <div @click.prevent="exporttopdf()">Export Chart to PDF</div>
            <div @click.prevent="exportDataPdf()">Export Data to PDF</div>
          </div>
        </div>
      </div>
    </div>

    <div class="stat-body-white chart-container d-flex flex-column pt-0" ref="containerBlock">
      <div v-if="loader" class="loader"></div>

      <div v-if="!loader && selectedGroups.length" class="selectdrill d-flex flex-row-reverse gap-2 w-100">
        <img class="cursor-pointer" src="../../assets/img/chart_cancel.png" @click.prevent="cancelselection()" alt="Cancel selection" />
        <img class="cursor-pointer" src="../../assets/img/chart_submit.png" @click.prevent="recreatechart()" alt="Apply selection" />
      </div>

      <svg v-if="!loader" ref="chartContainer" viewBox="0 0 500 500"></svg>
      <div ref="tooltip" class="tooltip"></div>
    </div>

    <enlargerpopup v-if="expandBlock == true" @closeModal="(expandBlock = false), updateChart()">
      <div class="stat-body-white d-flex flex-column w-100">
        <div v-if="!loader && selectedGroups.length" class="selectdrill d-flex flex-row-reverse gap-2 w-100">
          <img class="cursor-pointer" src="../../assets/img/chart_cancel.png" @click.prevent="cancelselection()" alt="Cancel selection" />
          <img class="cursor-pointer" src="../../assets/img/chart_submit.png" @click.prevent="recreatechart()" alt="Apply selection" />
        </div>
        <svg ref="popupchartContainer"></svg>
      </div>
    </enlargerpopup>
  </div>
</template>

<script>
import * as d3 from "d3";
import enlargerpopup from "../AiCharts/popupfiles/enlarge_popup.vue";
import html2pdf from "html2pdf.js";

export default {
  name: "etmfpiechart",
  components: { enlargerpopup },
  props: {
    data: { type: [Array, Object], required: true, default: () => [] },
    templateId: { type: String, default: "" },
    fromPage: { type: String, default: "descriptiveStatistics" },
    enableselectoption: { type: Boolean, default: false },
    colorcodes: { type: Array, default: () => [] },
    customColors: {
      type: Array,
      default: () => ["#28C0FF", "#FF98BD", "#8C82FF", "#FFB44F", "#57D9A5", "#8CE3CF", "#C9A0DC", "#FFA07A"]
    },
    roleModeEnabled: { type: Boolean, default: true },
    chartlabel: { type: String, default: "Status Distribution" },
    loader: { type: Boolean, default: false },

    /* Donut options */
    donutInnerRatio: { type: Number, default: 0.55 }, // innerRadius = radius * donutInnerRatio
    showCenterTotal: { type: Boolean, default: true },
    centerTitle: { type: String, default: "" },       // optional small label over total
  },
  computed: {
    computedChartLabel() { return this.chartlabel; },
  },
  data() {
    return {
      selectedRoleKey: "approval",
      roleOptions: [
        { value: "approval", label: "Approval" },
        { value: "reviewer", label: "Review" },
        { value: "qc", label: "QC" },
        { value: "sign", label: "Sign" },
      ],
      expandBlock: false,
      showVisitPopup: false,
      colormapping: {},     // { __role__: { [label]: color } }
      selectedGroups: [],   // selected labels for highlight & drilldown
      chartdataRows: [],
      tooltip: null,
      resizeObserver: null,
      lastTotals: { total: 0 }, // keep for center text
    };
  },
  watch: {
    data: {
      handler() { 
        this.safeRedraw(); 
        this.cancelselection();
      },
      deep: true,
    },
    selectedRoleKey() {
      delete this.colormapping["__role__"];
      this.cancelselection();
      this.safeRedraw();
    },
    expandBlock(nv) { if (nv) this.$nextTick(() => this.createChart()); },
  },
  mounted() {
    this.tooltip = this.$refs.tooltip;
    this.$nextTick(() => this.createChart());

    this.resizeObserver = new ResizeObserver(() => this.updateChart());
    if (this.$refs.containerBlock) this.resizeObserver.observe(this.$refs.containerBlock);
  },
  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    /* ===== utils ===== */
    norm(s) { return String(s == null ? "" : s).trim().toLowerCase(); },
    rawEntities() {
      if (Array.isArray(this.data)) return this.data;
      if (this.data && Array.isArray(this.data.entities)) return this.data.entities;
      return [];
    },
    ensureColorBucket(colorKey) {
      if (!this.colormapping[colorKey]) this.colormapping[colorKey] = {};
      return this.colormapping[colorKey];
    },
    getColorFor(colorKey, label, indexHint) {
      const bucket = this.ensureColorBucket(colorKey);
      if (bucket[label]) return bucket[label];

      const byType = {};
      for (let i = 0; i < (this.colorcodes || []).length; i++) {
        const c = this.colorcodes[i];
        byType[c.type] = c.color;
      }
      if (byType[label]) {
        bucket[label] = byType[label];
        return bucket[label];
      }

      const used = {};
      const vals = Object.values(bucket);
      for (let i = 0; i < vals.length; i++) used[vals[i]] = true;

      const palette = this.customColors && this.customColors.length ? this.customColors : ["#999"];
      if (typeof indexHint === "number" && indexHint < palette.length && !used[palette[indexHint]]) {
        bucket[label] = palette[indexHint];
        return bucket[label];
      }
      for (let j = 0; j < palette.length; j++) {
        const c = palette[j];
        if (!used[c]) { bucket[label] = c; return c; }
      }
      let attempt = 0;
      while (attempt < 360) {
        const color = "hsl(" + ((vals.length * 47 + attempt * 29) % 360) + ", 65%, 55%)";
        if (!used[color]) { bucket[label] = color; return color; }
        attempt++;
      }
      bucket[label] = "#999";
      return "#999";
    },

    /* ===== role-mode data ===== */
    getRoleAssignmentsFromRow(row, roleKey) {
      const out = [];
      function push(statusLabel, assignmentIndex) {
        const lbl = statusLabel != null ? String(statusLabel).trim() : "";
        if (!lbl) return;
        out.push({
          versionId: (row && row.documentVersion && (row.documentVersion.versionId || row.documentVersion.id)) ? String(row.documentVersion.versionId || row.documentVersion.id) : "",
          statusLabel: lbl,
          roleKey: roleKey,
          originalRow: row,
          assignmentIndex: assignmentIndex
        });
      }

      const dv = row && row.documentVersion ? row.documentVersion : null;
      if (roleKey === "approval") {
        const arrA = dv && dv.approval ? (Array.isArray(dv.approval) ? dv.approval : [dv.approval]) : [];
        for (let i = 0; i < arrA.length; i++) push(arrA[i]?.approvedStatus || arrA[i]?.status || arrA[i]?.approvalStatus, i);
        return out;
      }
      if (roleKey === "reviewer") {
        const arrR = dv && dv.reviewer ? (Array.isArray(dv.reviewer) ? dv.reviewer : [dv.reviewer]) : [];
        for (let j = 0; j < arrR.length; j++) push(arrR[j]?.reviewedStatus || arrR[j]?.status || arrR[j]?.reviewStatus, j);
        return out;
      }
      if (roleKey === "qc") {
        const arrQ = dv && dv.qc ? (Array.isArray(dv.qc) ? dv.qc : [dv.qc]) : [];
        for (let k = 0; k < arrQ.length; k++) push(arrQ[k]?.qcStatus || arrQ[k]?.status || arrQ[k]?.qualityStatus, k);
        return out;
      }

      if (roleKey === "sign") {
        const items = row && Array.isArray(row.docusignItems) ? row.docusignItems : [];
        for (let di = 0; di < items.length; di++) {
          const item = items[di] || {};
          const recips = item && item.recipients ? (Array.isArray(item.recipients) ? item.recipients : [item.recipients]) : [];
          for (let ri = 0; ri < recips.length; ri++) {
            const recip = recips[ri] || {};
            push(recip.recipientStatus, "ds:" + di + ":" + ri);
          }
        }
        return out;
      }
      return out;
    },
    getBuckets(raw, roleKey) {
      const assignments = [];
      for (let i = 0; i < raw.length; i++) {
        const arr = this.getRoleAssignmentsFromRow(raw[i], roleKey);
        for (let j = 0; j < arr.length; j++) assignments.push(arr[j]);
      }
      const map = {};
      for (let k = 0; k < assignments.length; k++) {
        const e = assignments[k];
        const key = e.statusLabel;
        if (!map[key]) map[key] = { key: key, value: 0, subarray: [] };
        map[key].value++;
        map[key].subarray.push(e);
      }
      const rolled = [];
      for (const k in map) if (Object.prototype.hasOwnProperty.call(map, k)) rolled.push(map[k]);
      const total = d3.sum(rolled, (d) => d.value);
      return { rolled, total };
    },

    /* ===== actions ===== */
    exportDataPdf() { this.$emit("exportDataPdf"); },
    async exporttoexcell() { this.$emit("exporttoexcell"); },
    exporttopdf() {
      const excludeElement = this.$refs.excludeThis;
      if (excludeElement) excludeElement.style.display = "none";
      const pdfContainer = document.createElement("div");
      const captureArea = this.$refs.captureArea.cloneNode(true);
      pdfContainer.appendChild(captureArea);
      const opt = { margin: 0, filename: (this.computedChartLabel || "chart_data") + ".pdf", image: { type: "jpeg", quality: 0.98 }, html2canvas: { scale: 2 }, jsPDF: { unit: "pt", format: "a4", orientation: "landscape" } };
      html2pdf().set(opt).from(pdfContainer).save();
    },
    showtable() {
      const payload = this.chartdataRows && this.chartdataRows.length ? this.chartdataRows : this.rawEntities();
      this.$emit("opendrillpopup", payload);
    },
    cancelselection() { this.selectedGroups = []; this.updateChart(); },

    /* ===== drawing ===== */
    safeRedraw() { this.$nextTick(() => this.updateChart()); },
    createChart() {
      const svgEl = this.expandBlock ? this.$refs.popupchartContainer : this.$refs.chartContainer;
      if (!svgEl) return;

      const raw = this.rawEntities();
      const { rolled, total } = this.getBuckets(raw, this.selectedRoleKey);

      this.lastTotals.total = total;

      this.chartdataRows = [];
      for (let i = 0; i < rolled.length; i++) {
        const d = rolled[i];
        this.chartdataRows.push({
          label: String(d.key),
          count: d.value,
          percentage: Number(((d.value / (total || 1)) * 100).toFixed(2))
        });
      }

      d3.select(svgEl).selectAll("*").remove();
      if (!rolled.length || !total) return;

      this.drawDonut(svgEl, rolled, total, "__role__");
    },
    updateChart() {
      const svg = this.expandBlock ? this.$refs.popupchartContainer : this.$refs.chartContainer;
      if (!svg) return;
      d3.select(svg).selectAll("*").remove();
      this.createChart();
    },

    /* ===== DONUT drawing ===== */
    drawDonut(svgEl, rolled, total, colorKey) {
      // ----- legend labels -----
      const labels = rolled.map(d => {
        const pct = ((d.value / total) * 100).toFixed(2);
        return `${d.key}: ${d.value} (${pct}%)`;
      });

      // ----- measure legend widths -----
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      ctx.font = "12px sans-serif";
      const textWidths = labels.map(t => ctx.measureText(t).width);
      const maxTextW = Math.max(0, ...textWidths);

      // ----- responsive layout -----
      const width = (this.$refs.containerBlock && this.$refs.containerBlock.clientWidth) || 500;
      const pieHeight = 280;
      const swatchW = 18;
      const gap = 6;
      const pad = 16;
      const lineH = 24;

      const itemW = swatchW + gap + maxTextW + pad;
      const perRow = Math.max(1, Math.floor(width / Math.max(1, itemW)));
      const rows = Math.ceil(rolled.length / perRow);
      const legendH = rows * lineH + 10;

      const svgHeight = pieHeight + legendH + 20;
      const radius = Math.max(40, Math.min(width, pieHeight) / 2 - 20);
      const innerRadius = Math.max(10, Math.floor(radius * Math.min(Math.max(this.donutInnerRatio, 0.1), 0.9)));

      // ----- svg root + groups -----
      const root = d3.select(svgEl).attr("viewBox", `0 0 ${width} ${svgHeight}`);
      const g = root.append("g").attr("transform", `translate(${width / 2},${pieHeight / 2})`);

      // ----- generators -----
      const pieGen = d3.pie().sort(null).value(d => d.value);
      const arcGen = d3.arc().innerRadius(innerRadius).outerRadius(radius);

      // ----- slices -----
      const arcs = g.selectAll(".arc")
        .data(pieGen(rolled))
        .enter()
        .append("g")
        .attr("class", "arc")
        .on("mouseover", (event, d) => {
          const pct = ((d.value / total) * 100).toFixed(2);
          d3.select(this.tooltip)
            .style("opacity", 1)
            .html(`Category: ${d.data.key}<br/>Value: ${d.data.value}<br/>${pct}%`)
            .style("left", (event.clientX + 12) + "px")
            .style("top", (event.clientY + 12) + "px");
        })
        .on("mouseout", () => d3.select(this.tooltip).style("opacity", 0))
        .on("mousemove", (event) => {
          d3.select(this.tooltip)
            .style("left", (event.clientX + 12) + "px")
            .style("top", (event.clientY + 12) + "px");
        })
        .on("click", (_event, d) => this.onSliceClick(d.data.key));

      arcs.append("path")
        .attr("d", arcGen)
        .attr("fill", (d, i) => this.getColorFor(colorKey, d.data.key, i))
        .attr("stroke", "#fff")
        .attr("stroke-width", d => this.selectedGroups.includes(String(d.data.key)) ? 3 : 1.5)
        .attr("opacity", d => {
          if (!this.selectedGroups.length) return 1;
          return this.selectedGroups.includes(String(d.data.key)) ? 1 : 0.35;
        });

      // ----- center text (title + total) -----
      if (this.showCenterTotal) {
        const center = g.append("g").attr("class", "center-label").attr("text-anchor", "middle");
        const topLabel = this.centerTitle || this.computedChartLabel || "";
        if (topLabel) {
          center.append("text")
            .attr("dy", "-0.3em")
            .style("font", "12px sans-serif")
            .style("fill", "#6b7280")
            .text(topLabel);
        }
        center.append("text")
          .attr("dy", topLabel ? "1.1em" : "0.35em")
          .style("font-weight", 700)
          .style("font-size", "18px")
          .text(total);
      }

      // ----- legend -----
      const legendY = pieHeight - (lineH / 2) + 10;
      const legend = root.append("g").attr("class", "legend");

      const items = legend.selectAll(".legend-item")
        .data(rolled)
        .enter()
        .append("g")
        .attr("class", "legend-item")
        .attr("transform", (_d, i) => {
          const row = Math.floor(i / perRow);
          const col = i % perRow;
          const totalRowWidth = Math.min(perRow, rolled.length - row * perRow) * itemW;
          const rowLeftX = (width - totalRowWidth) / 2;
          const x = rowLeftX + col * itemW;
          const y = legendY + row * lineH;
          return `translate(${x},${y})`;
        })
        .style("opacity", d => {
          if (!this.selectedGroups.length) return 1;
          return this.selectedGroups.includes(String(d.key)) ? 1 : 0.35;
        })
        .on("click", (_e, d) => this.onSliceClick(d.key));

      items.append("rect")
        .attr("width", swatchW)
        .attr("height", swatchW)
        .attr("y", -swatchW / 2)
        .attr("fill", (d, i) => this.getColorFor(colorKey, d.key, i))
        .attr("stroke", "#fff")
        .attr("stroke-width", 0.8);

      items.append("text")
        .attr("x", swatchW + gap)
        .attr("dy", "0.35em")
        .style("font", "12px sans-serif")
        .text((_, i) => labels[i]);
    },

    onSliceClick(label) {
      const key = String(label).trim();
      const idx = this.selectedGroups.indexOf(key);
      if (idx !== -1) this.selectedGroups.splice(idx, 1);
      else this.selectedGroups.push(key);
      this.updateChart();
    },

    /* ===== drilldown ===== */
    recreatechart() {
      const raw = this.rawEntities();
      const selected = this.selectedGroups.slice();
      const selectedSet = {};
      for (let i = 0; i < selected.length; i++) selectedSet[this.norm(selected[i])] = true;

      const roleKey = this.selectedRoleKey; 
      const roleField = (roleKey === "approval")
        ? "approval"
        : (roleKey === "reviewer")
          ? "reviewer"
          : (roleKey === "qc")
            ? "qc"
            : "sign";

      const filteredRows = [];

      for (let r = 0; r < raw.length; r++) {
        const row = raw[r];

        if (roleField === "sign") {
          const items = (row && Array.isArray(row.docusignItems)) ? row.docusignItems : [];
          const keptItems = [];

          for (let di = 0; di < items.length; di++) {
            const item = items[di] || {};
            const recips = item && item.recipients
              ? (Array.isArray(item.recipients) ? item.recipients : [item.recipients])
              : [];
            const keptRecips = [];

            for (let ri = 0; ri < recips.length; ri++) {
              const recip = recips[ri] || {};
              const recipStatus = recip.recipientStatus;
              const matchSign = (selected.length === 0) ? true : !!selectedSet[this.norm(recipStatus)];
              if (matchSign) keptRecips.push(recip);
            }

            if (keptRecips.length > 0) {
              const newItem = Object.assign({}, item, { recipients: keptRecips });
              keptItems.push(newItem);
            }
          }

          if (keptItems.length > 0) {
            const newRowSign = Object.assign({}, row, { docusignItems: keptItems });
            filteredRows.push(newRowSign);
          }
        } else {
          const dv = row && row.documentVersion ? row.documentVersion : null;
          if (!dv) continue;

          const arr = dv[roleField]
            ? (Array.isArray(dv[roleField]) ? dv[roleField] : [dv[roleField]])
            : [];
          if (!arr.length) continue;

          const keep = [];
          for (let a = 0; a < arr.length; a++) {
            const entry = arr[a] || {};
            let roleStatus = "";
            if (roleKey === "approval") roleStatus = entry.approvedStatus || entry.status || entry.approvalStatus;
            else if (roleKey === "reviewer") roleStatus = entry.reviewedStatus || entry.status || entry.reviewStatus;
            else roleStatus = entry.qcStatus || entry.status || entry.qualityStatus;

            const matchRole = (selected.length === 0) ? true : !!selectedSet[this.norm(roleStatus)];
            if (matchRole) keep.push(entry);
          }

          if (keep.length > 0) {
            const newDv = Object.assign({}, dv);
            newDv[roleField] = keep;
            const newRowRole = Object.assign({}, row, { documentVersion: newDv });
            filteredRows.push(newRowRole);
          }
        }
      }

      const filterkeyword = roleKey + "(" + (selected.length ? selected.join(",") : "All") + ")";
      this.$emit("drilldowncurrentchart", { sortedData: filteredRows, filterkeyword });
    },
  },
};
</script>

<style scoped>
@import "../AiCharts/charts.css";

.legend { text-anchor: start; }
.loader {
  border: 6px solid #f3f3f3; border-top: 6px solid #3498db; border-radius: 50%;
  width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto;
}
@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
.graph-select { min-width: 160px; }
.tooltip { position: fixed; z-index: 9999; pointer-events: none; opacity: 0; padding: 6px 8px; background: rgba(0,0,0,0.75); color: #fff; border-radius: 4px; font-size: 12px; }

.chart-container {
  overflow-x: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  min-height: 300px;
}
.chart-container svg {
  flex: 1 1 auto;
  min-height: 300px;
  width: 100%;
  display: block;
}
</style>
